/* eslint-disable import/no-dynamic-require */
/* eslint-disable global-require */
import React from 'react';
import { IntlProvider } from 'react-intl';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
// Models
import mProduct from 'models/product';
// Custom
import { initLocale } from 'utils/DateUtils';
import useBrowserLocale from 'hooks/useBrowserLocale';
import { changeLocale } from 'actions';
import {
  authShape,
  meDataShape,
  errorShape,
} from 'constants/PropTypesShapes';
import localeManager from './LocalesManager';

const keySelector = (intl) => `${intl.locale}-${JSON.stringify(intl).length}`;
const product = __PRODUCT__; // eslint-disable-line no-undef

const ConnectedIntlProvider = ({
  intl: { locale: localeFromStore },
  auth: { loggedIn },
  me,

  children,
  dispatchChangeLocale,
}) => {
  const [allTranslations, setAllTranslations] = React.useState({});
  const [loadingTranslations, setLoadingTranslations] = React.useState(true);

  const defaultLocale = localeManager.getDefaultLocale();
  const browserLocale = useBrowserLocale();
  const productAvailableLocales = localeManager.getAvailableLocales().filter(({ locale }) => mProduct.getAuthorizedLocales(product).includes(locale));

  /* ========================================================= */
  /* EFFECTS                                                   */
  /* ========================================================= */
  React.useEffect(() => {
    // Load translations files each time locale changes
    const loadTranslations = async () => {
      // 🖼️ [fr-FR, en-EN, es-ES]
      const localesForProduct = productAvailableLocales.map(({ locale }) => locale);

      const isAlreadyLoaded = allTranslations[localeFromStore];
      const areTranslationsAvailable = localesForProduct.includes(localeFromStore);

      if (areTranslationsAvailable && !isAlreadyLoaded) {
        setLoadingTranslations(true);
        const translationPromises = await Promise.all(
          mProduct.getTradFilesNames(product).map((tradFileName) => import(`assets/langs/${localeFromStore}/${tradFileName}`)),
        );
        const newTranslations = Object.assign({}, ...translationPromises);
        setAllTranslations((translations) => ({ ...translations, [localeFromStore]: newTranslations }));
        setLoadingTranslations(false);
      }
    };
    loadTranslations();
    initLocale(localeFromStore);
  }, [localeFromStore]);
  /* ✅ ⬇️ */
  React.useEffect(function setLocaleIfNotLoggedIn() {
    // If the user is not logged in and no locale is already set in store, browser locale is used if it is supported by the app (otherwise default locale)
    // If the user is logged in, its preferred language is read from me data returned by backend once logged in so no need to refer to browser current language
    if (!loggedIn && browserLocale) {
      const localeBrowser = productAvailableLocales.find(({ matchingPattern }) => matchingPattern.test(browserLocale));
      const localeAvailableForApp = productAvailableLocales.some(({ locale }) => locale === localeFromStore);
      let localeToUse = '';
      // First try to get locale from store
      if (localeFromStore && localeAvailableForApp) {
        localeToUse = localeFromStore;
      // If not, use first locale compatible with browser locale
      } else if (localeBrowser) {
        localeToUse = localeBrowser.locale;
      // Else use default locale
      } else {
        localeToUse = defaultLocale.locale;
      }

      dispatchChangeLocale(localeToUse);
    }
  }, [allTranslations, browserLocale]);
  /* ✅ ⬇️ */
  React.useEffect(() => {
    if (me && me.data && me.data.locale) {
      const { locale: accountLocale } = me.data;
      if (accountLocale) {
        const accountLocaleAvailableForApp = productAvailableLocales.some(({ locale }) => locale === accountLocale);
        if (accountLocaleAvailableForApp && localeFromStore !== accountLocale) {
          dispatchChangeLocale(accountLocale);
        }
      }
    }
  }, [me]);

  /* ========================================================= */
  /* UI                                                        */
  /* ========================================================= */
  if (loadingTranslations || !localeFromStore) {
    return null;
  }

  return (
    <IntlProvider
      locale={localeFromStore}
      key={localeFromStore}
      keySelector={keySelector}
      messages={allTranslations[localeFromStore]}
      defaultLocale={defaultLocale}
    >
      { children }
    </IntlProvider>
  );
};

ConnectedIntlProvider.propTypes = {
  auth: authShape.isRequired,
  intl: PropTypes.shape({
    locale: PropTypes.string.isRequired,
  }).isRequired,
  me: PropTypes.shape({
    error: errorShape,
    loading: PropTypes.bool.isRequired,
    logout: PropTypes.bool.isRequired,
    data: meDataShape,
  }).isRequired,
  children: PropTypes.element.isRequired,
  dispatchChangeLocale: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  auth: state.auth,
  intl: state.intl,
  me: state.me,

});

const mapDispatchToProps = (dispatch) => ({
  dispatchChangeLocale: (locale) => {
    dispatch(changeLocale(locale));
  },
});

module.exports = connect(
  mapStateToProps,
  mapDispatchToProps,
  null,
)(ConnectedIntlProvider);



// WEBPACK FOOTER //
// ./intl/ConnectedIntlProvider.js